import React from 'react';
import PropTypes from 'prop-types';
import { useController, useFormState } from 'react-hook-form';
import { endOfDay } from 'date-fns';
import { Themes } from '../../style/theme-consts';
import { Autocomplete, RenderValueTypes, Select, AsyncAutocomplete } from '../Select';
import { TextFieldInput } from '../TextFieldInput';
import { DatePicker, DateRangeSelectInline } from '../DateTime';
import { TabsSwitcher } from '../TabsSwitcher';
import { Checkbox } from '../Checkbox';
import { Toggle } from '../Toggle';
import RadioGroup from '../RadioGroup';
import { InputHeights } from '../../utils/consts';
import * as S from './styled';

const EMPTY_ARRAY = [];
/** used in-case control is disabled and instead of showing select value, name/label is needed.
 * For example, in read ony mode, we show Text */
const getValue = (value, valueToName) => {
  if (!valueToName) return value;
  let result;
  if (typeof valueToName === 'function') result = valueToName(value);
  else if (Array.isArray(valueToName)) result = valueToName.find(item => item.value === value)?.label;
  else if (typeof valueToName === 'object') result = valueToName[value];

  return result || value;
};

const ControlError = ({ message }) => {
  // const { isCropped, componentRef } = useIsComponentCropped(error?.message);
  // render CustomTooltip if cropped
  if (!message) return null;
  return <S.Error title={message}>{message}</S.Error>;
};
ControlError.propTypes = {
  message: PropTypes.node
};

export const FormErrors = ({ control, names }) => {
  const { errors } = useFormState({ control });
  const fields = Object.keys(errors);
  if (fields.length === 0) return null;
  return fields.filter(f => !names || names?.includes(f)).map(f => <ControlError message={errors[f]?.message} />);
};
FormErrors.propTypes = {
  control: PropTypes.shape({}),
  names: PropTypes.arrayOf(PropTypes.string)
};

export const AutocompleteControl = ({
  name,
  label,
  mode,
  options,
  freeSolo,
  multiple,
  disabled,
  clearable,
  control,
  renderValue,
  innerLabel = false,
  width = S.CONTROL_WIDTH,
  placeholder = '',
  virtualize = false
}) => {
  const { field, fieldState } = useController({ name, control });
  const normalizedValue = multiple ? field.value || EMPTY_ARRAY : field.value ?? null;

  return (
    <S.FieldContainer>
      {!innerLabel && label}
      <Autocomplete
        label={innerLabel && label}
        multiple={multiple}
        disabled={disabled}
        clearable={clearable}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={normalizedValue}
        testId={`select-${name}`}
        options={options}
        mode={mode}
        freeSolo={freeSolo}
        disablePortal={false}
        width={width}
        height={InputHeights.LARGE}
        placeholder={placeholder}
        renderValue={renderValue}
        virtualize={virtualize}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};
AutocompleteControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
      dotColor: PropTypes.string,
      children: PropTypes.array
    })
  ).isRequired,
  mode: PropTypes.oneOf([Themes.DARK, Themes.LIGHT]),
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  freeSolo: PropTypes.bool,
  control: PropTypes.shape({}),
  innerLabel: PropTypes.bool,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  renderValue: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf(['CHIPS', 'COMMA_SEPARATED', 'COUNT'])]),
  virtualize: PropTypes.bool
};

export const SelectControl = ({
  name,
  label,
  options,
  multiple,
  renderValue,
  disabled,
  clearable,
  control,
  placeholder,
  width = S.CONTROL_WIDTH
}) => {
  const { field, fieldState } = useController({ name, control });
  const normalizedValue = multiple ? field.value || EMPTY_ARRAY : field.value ?? null;

  return (
    <S.FieldContainer $width={width}>
      {label}
      <Select
        clearable={clearable}
        multiple={multiple}
        renderValue={renderValue}
        disabled={disabled}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={normalizedValue}
        testId={`select-${name}`}
        options={options}
        width={width}
        height={InputHeights.LARGE}
        placeholder={placeholder}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};
SelectControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  width: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      icon: PropTypes.oneOfType([PropTypes.node, PropTypes.element]),
      dotColor: PropTypes.string,
      children: PropTypes.array
    })
  ).isRequired,
  multiple: PropTypes.bool,
  renderValue: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.oneOf([RenderValueTypes.CHIPS, RenderValueTypes.COMMA_SEPARATED, RenderValueTypes.COUNT])
  ]),
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  placeholder: PropTypes.string,
  control: PropTypes.shape({})
};

export const InputControl = ({
  name,
  label,
  rows,
  disabled,
  placeholder,
  control,
  type,
  valueToName,
  InputProps,
  width = S.CONTROL_WIDTH,
  height = InputHeights.LARGE,
  autoComplete
}) => {
  const { field, fieldState } = useController({ name, control });
  return (
    <S.FieldContainer $width={width}>
      {label}
      <TextFieldInput
        type={type}
        // error={fieldState.error?.message} - ask NASER
        disabled={disabled}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={getValue(field.value, valueToName)}
        rows={rows}
        isMultiline={rows > 1}
        testId={`input-${name}`}
        width={width}
        height={height}
        placeholder={placeholder}
        InputProps={InputProps}
        autoComplete={autoComplete}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};
InputControl.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.oneOf(['DYNAMIC', 'LARGE', 'MEDIUM', 'SMALL', 'TINY']),
  label: PropTypes.node,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  InputProps: PropTypes.shape({}),
  autoComplete: PropTypes.string,
  valueToName: PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.node }))
  ]),
  control: PropTypes.shape({})
};

export const RadioControl = ({ name, label, options, control, width = S.CONTROL_WIDTH }) => {
  const { field, fieldState } = useController({ name, control });
  return (
    <S.FieldContainer $width={width}>
      {label}
      <RadioGroup onChange={field.onChange} value={field.value} testId={`radio-${name}`} options={options} />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};
RadioControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      tooltip: PropTypes.node
    })
  ).isRequired,
  control: PropTypes.shape({})
};

export const DateControl = ({ name, label, disabled, clearable, control }) => {
  const { field, fieldState } = useController({ name, control });
  return (
    <S.FieldContainer>
      {label}
      <DatePicker
        disabled={disabled}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={field.value}
        clearable={clearable}
        testId={`input-${name}`}
        width={S.CONTROL_WIDTH}
        height={InputHeights.LARGE}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

DateControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  control: PropTypes.shape({})
};

export const DateRangeControl = ({ name, label, options, disabled, clearable, control, innerLabel = false }) => {
  const { field, fieldState } = useController({ name, control });

  const onChange = val => {
    field.onChange({
      ...val,
      ...(val?.to ? { to: endOfDay(new Date(val.to)).getTime() } : {})
    });
  };

  return (
    <S.FieldContainer>
      {!innerLabel && label}
      <DateRangeSelectInline
        label={innerLabel && label}
        disabled={disabled}
        clearable={clearable}
        onChange={onChange}
        onBlur={field.onBlur}
        value={field.value}
        options={options}
        testId={`input-${name}`}
        width={S.CONTROL_WIDTH}
        height={InputHeights.LARGE}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

DateRangeControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any.isRequired,
      unit: PropTypes.string.isRequired,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
    })
  ).isRequired,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  control: PropTypes.shape({}),
  innerLabel: PropTypes.bool
};

export const TabsSwitcherControl = ({ name, control, label, options, shape, width = S.CONTROL_WIDTH }) => {
  const { field, fieldState } = useController({ name, control });

  const onChange = (_, val) => field.onChange(val);

  return (
    <S.FieldContainer $width={width}>
      {label}
      <TabsSwitcher onChange={onChange} value={field.value} options={options} testId={`tab-${name}`} shape={shape} />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

TabsSwitcherControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      value: PropTypes.any
    })
  ).isRequired,
  control: PropTypes.shape({}),
  shape: PropTypes.oneOf(['OUTLINED', 'FLAT', 'FILLED', 'FILTER']),
  width: PropTypes.string
};

export const AsyncAutocompleteControl = ({
  name,
  label,
  fetchOptionsData,
  multiple,
  disabled,
  clearable,
  control,
  minLength,
  delay,
  renderValue,
  chipsCount,
  innerLabel = false,
  width = S.CONTROL_WIDTH,
  disablePortal
}) => {
  const { field, fieldState } = useController({ name, control });
  const normalizedValue = multiple ? field.value || EMPTY_ARRAY : field.value ?? null;

  return (
    <S.FieldContainer>
      {!innerLabel && label}
      <AsyncAutocomplete
        label={innerLabel && label}
        multiple={multiple}
        disabled={disabled}
        clearable={clearable}
        onChange={field.onChange}
        onBlur={field.onBlur}
        value={normalizedValue}
        testId={`async-select-${name}`}
        fetchOptionsData={fetchOptionsData}
        width={width}
        height={InputHeights.LARGE}
        minLength={minLength}
        delay={delay}
        renderValue={renderValue}
        chipsCount={chipsCount}
        disablePortal={disablePortal}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

AsyncAutocompleteControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  fetchOptionsData: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  control: PropTypes.shape({}),
  minLength: PropTypes.number,
  delay: PropTypes.number,
  renderValue: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.oneOf([RenderValueTypes.CHIPS, RenderValueTypes.COMMA_SEPARATED, RenderValueTypes.COUNT])
  ]),
  chipsCount: PropTypes.number,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disablePortal: PropTypes.bool
};

export const CheckboxControl = ({ name, label, disabled, control }) => {
  const { field, fieldState } = useController({ name, control });

  return (
    <S.FieldContainer>
      <Checkbox
        disabled={disabled}
        onChange={field.onChange}
        onBlur={field.onBlur}
        checked={field.value || false}
        label={label}
        testId={`checkbox-${name}`}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

CheckboxControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  control: PropTypes.shape({})
};

export const ToggleControl = ({ name, label, disabled, control, testId, width = 'auto' }) => {
  const { field, fieldState } = useController({ name, control });
  return (
    <S.FieldContainer $width={width}>
      <Toggle
        disabled={disabled}
        onBlur={field.onBlur}
        value={field.value || false}
        setValue={field.onChange}
        label={label}
        testId={testId || `toggle-${name}`}
      />
      <ControlError message={fieldState.error?.message} />
    </S.FieldContainer>
  );
};

ToggleControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  disabled: PropTypes.bool,
  control: PropTypes.shape({}),
  testId: PropTypes.string,
  width: PropTypes.string
};
